import { format, parseISO } from "date-fns";

import { StackedBar } from "~/components/charts/bar";
import { abbreviatedNum } from "~/lib/abbreviated-num";
import type { Attestation } from "~/lib/stablecoins.server";

const totalInCirculation = "Total in Circulation";
const cashAndEquivalents = "Cash & Equivalents";
const usTreasuries = "Treasuries";

const ReservesData = new Map([
  [
    "SBC",
    {
      labels: [totalInCirculation, "Reserves"],
      values: [
        [totalInCirculation, [6220295.18, 0]],
        [cashAndEquivalents, [0, 1274999.28]],
        [usTreasuries, [0, 4945294.9]],
      ],
    },
  ],
  [
    "USDGLO",
    {
      labels: [totalInCirculation, "Reserves"],
      values: [
        [totalInCirculation, [1503485.56, 0]],
        [cashAndEquivalents, [0, 308175.81]],
        [usTreasuries, [0, 1195309.75]],
      ],
    },
  ],
  [
    "USC",
    {
      labels: [totalInCirculation, "Reserves"],
      values: [
        [totalInCirculation, [15013, 0]],
        [cashAndEquivalents, [0, 7507]],
        [usTreasuries, [0, 7506]],
      ],
    },
  ],
  [
    "MXNe",
    {
      labels: [totalInCirculation, "Reserves"],
      values: [
        [totalInCirculation, [15013, 0]],
        [cashAndEquivalents, [0, 7507]],
        [usTreasuries, [0, 7506]],
      ],
    },
  ],
]);

export const ReservesChart = (ticker: string, attestations: Attestation[]) => {
  const latestAttestation = attestations.at(-1);

  const data = latestAttestation
    ? toChartData(latestAttestation)
    : ReservesData.get(ticker);

  return data ? (
    <div>
      <h3 className="h4 mt-0 mb-3">
        Reserves as of{" "}
        {latestAttestation
          ? format(parseISO(latestAttestation.date), "MMMM d, yyyy")
          : "September 30, 2023"}
      </h3>
      <div className="relative h-[30vh] w-full">
        {data && (
          <StackedBar
            labels={data.labels}
            options={{
              indexAxis: "x",
              plugins: {
                legend: { labels: { boxHeight: 5, usePointStyle: true } },
                tooltip: {
                  callbacks: {
                    label: (ctx) => {
                      let label = ctx.dataset.label ?? "";
                      if (label) label += ": ";
                      if (ctx.parsed.y != null) {
                        label += `$${ctx.parsed.y.toFixed(2)}`;
                      }
                      return label;
                    },
                  },
                },
              },
              responsive: true,
              maintainAspectRatio: false,
              scales: {
                x: { stacked: true, display: true },
                y: {
                  stacked: true,
                  grid: { color: "rgba(100, 100, 100, 0.40" },
                  ticks: {
                    callback: (v) =>
                      typeof v === "number" ? `$${abbreviatedNum(v)}` : v,
                  },
                },
              },
            }}
            values={Object.fromEntries(data.values)}
            datasetOptions={{ fill: true, borderWidth: 2, borderRadius: 8 }}
            barThickness={100}
          />
        )}
      </div>
    </div>
  ) : null;

  function toChartData(attestation: Attestation) {
    return {
      labels: [totalInCirculation, "Reserves"],
      values: [
        [totalInCirculation, [attestation.in_circulation, 0]],
        [cashAndEquivalents, [0, attestation.cash_equivalents_balance]],
        [usTreasuries, [0, attestation.treasuries_balance]],
      ],
    };
  }
};
